// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryItem, { CategoryItemStyles } from "@pacific/App/Components/Categories/CategoryItem";
import ViewMoreCategoriesCard, {
    ViewMoreCategoriesCardStyles,
} from "@pacific/App/Components/Categories/ViewMoreCategoryCard";
import SectionWrapper, { SectionWrapperStyles } from "@pacific/App/Components/Common/SectionWrapper";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import { xScreen } from "@pacific/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum tabFields {
    title = "title",
    subCategories = "subCategories",
    showAllProductsCard = "showAllProductsCard",
    showAllProductsUrl = "showAllProductsUrl",
}

const enum fields {
    tabs = "tabs",
    title = "title",
}

interface TabProps {
    title: string;
    showAllProductsCard: boolean;
    showAllProductsUrl: LinkFieldValue;
    subCategories: Category[];
}

interface TabsProps {
    fields: {
        title: string;
        showAllProductsCard: boolean;
        showAllProductsUrl: LinkFieldValue;
        subCategories: string[];
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: TabsProps[];
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        categoriesDataView: getCategoriesDataView(state),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ContentFeatureStyles {
    spinner?: LoadingSpinnerProps;
    sectionWrapperStyles?: SectionWrapperStyles;
    categoriesGridContainer?: GridContainerProps;
    categoryGridItem?: GridItemProps;
    subCategoryItemStyles?: CategoryItemStyles;
    centeringWrapper?: InjectableCss;
    selectCategoryStylesWrapper?: InjectableCss;
    viewMoreCardStyle?: ViewMoreCategoriesCardStyles;
}

export const shopByStyles: ContentFeatureStyles = {
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    sectionWrapperStyles: {
        title: {
            wrapper: {
                css: css`
                    margin-bottom: 45px;
                `,
            },
        },
    },
    categoriesGridContainer: {
        gap: 22,
        css: css``,
    },
    categoryGridItem: {
        width: [6, 3, 3, 2, 2],
    },
    subCategoryItemStyles: {
        wrapper: {
            css: css`
                width: 100%;
            `,
        },
        imageWrapper: {
            css: css`
                width: 100%;
            `,
        },
        image: {
            width: 198,
            height: 198,
            transformations: {
                tabletWidth: 166,
                mobileWidth: 152,
            },
            css: css`
                object-fit: cover;
                width: 198px;
                height: 198px;
                img {
                    width: 100%;
                }
                ${xScreen("lte", "md")} {
                    width: 166px;
                    height: 166px;
                }
                ${xScreen("lte", "sm")} {
                    width: 152px;
                    height: 152px;
                }
            `,
        } as any,
    },
    viewMoreCardStyle: {
        wrapper: {
            css: css`
                ${xScreen("lte", "md")} {
                    height: 168px;
                    width: 168px;
                }
                ${xScreen("lte", "sm")} {
                    height: 168px;
                    width: 168px;
                }
            `,
        },
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-direction: column;
        `,
    },
    selectCategoryStylesWrapper: {
        css: css`
            width: 262px;
            padding-bottom: 24px;
            ${xScreen("lt", "xs")} {
                width: 100%;
            }
        `,
    },
};

const styles = shopByStyles;

const CategoryTabs: FC<Props> = ({ id, fields, loadCategories, categoriesDataView }) => {
    const [tabCategories, setTabCategories] = useState<TabProps[]>([]);

    React.useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }, []);

    React.useEffect(() => {
        if (!categoriesDataView.isLoading && categoriesDataView.value) {
            const tabs = fields.tabs.map(tab => {
                return {
                    title: tab.fields.title,
                    subCategories: categoriesDataView.value?.filter(
                        category => tab.fields.subCategories.indexOf(category.id) > -1,
                    ),
                    showAllProductsCard: tab.fields.showAllProductsCard,
                    showAllProductsUrl: tab.fields.showAllProductsUrl,
                } as any;
            });
            setTabCategories(tabs);
        }
    }, [fields.tabs, categoriesDataView]);

    if (!fields.tabs || fields.tabs.length === 0 || tabCategories.length === 0) {
        return null;
    }

    if (categoriesDataView.isLoading || !categoriesDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper} data-test-selector="shopByCategory_Loading">
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <SectionWrapper
            title={translate(fields.title)}
            extendedStyles={styles.sectionWrapperStyles}
            data-test-selector="category-tabs"
        >
            <TabGroup current={tabCategories[0]?.title}>
                {tabCategories.map(tab => (
                    <Tab key={tab.title} headline={tab.title} tabKey={tab.title}>
                        <GridContainer {...styles.categoriesGridContainer}>
                            {tab.subCategories?.map((category: Category) => (
                                <GridItem key={category.id} {...styles.categoryGridItem}>
                                    <CategoryItem
                                        showImage={true}
                                        categoryId={category.id}
                                        extendedStyles={styles.subCategoryItemStyles}
                                    />
                                </GridItem>
                            ))}
                            {tab.showAllProductsCard && (
                                <GridItem {...styles.categoryGridItem}>
                                    <ViewMoreCategoriesCard
                                        extendedStyles={styles.viewMoreCardStyle}
                                        text={translate("View All Products")}
                                        link={tab.showAllProductsUrl}
                                    />
                                </GridItem>
                            )}
                        </GridContainer>
                    </Tab>
                ))}
            </TabGroup>
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategoryTabs),
    definition: {
        displayName: "Category Tabs",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.tabs,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: tabFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: tabFields.showAllProductsCard,
                        displayName: "Show all products card",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                    },
                    {
                        name: tabFields.showAllProductsUrl,
                        displayName: "Show all products Url",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                        isVisible: item => item.fields.showAllProductsCard,
                    },
                    {
                        name: tabFields.subCategories,
                        editorTemplate: "CategoriesField",
                        defaultValue: [],
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
